/**
 * MappingWorkbench.tsx — Account mapping ("the crosswalk")
 *
 * Displays generated mapping rules with plain-language change pills, supports
 * filtering, bulk approval of unchanged accounts, and a detail drawer.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  ConversionProject,
  MappingRule,
  generateMappings,
  getMappings,
  getMappingDetail,
  updateMapping,
  bulkConfirmMappings,
} from '../../services/conversionArchitectAPI';
import {
  c, font, eyebrow, panel, inputStyle, pill,
  btnPrimary, btnSecondary, btnGreen,
  CHANGE_TYPES, STATUS_PILLS,
} from './tokens';
import Toast from './Toast';

interface MappingWorkbenchProps {
  project: ConversionProject;
  onPhaseComplete: () => void;
  onRefresh: () => void;
}

const MAPPING_TYPES = ['ALL', 'DIRECT', 'SPLIT', 'CONSOLIDATE', 'ENRICH', 'REBOOK', 'UNMAPPED'] as const;

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 10,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  color: c.textSecondary,
  borderBottom: `1px solid ${c.border}`,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '9px 10px',
  borderBottom: `1px solid ${c.hairline}`,
  verticalAlign: 'middle',
};

function changePill(type: string): React.ReactNode {
  const t = CHANGE_TYPES[type];
  if (!t) return <span style={pill(c.fill2, c.tableLabel)}>{type}</span>;
  return <span title={t.tip} style={pill(t.bg, t.fg)}>{t.label}</span>;
}

function statusPill(status: string): React.ReactNode {
  const s = STATUS_PILLS[status];
  if (!s) return <span style={pill(c.fill2, c.tableLabel)}>{status}</span>;
  return <span style={pill(s.bg, s.fg, s.border)}>{s.label}</span>;
}

const MappingWorkbench: React.FC<MappingWorkbenchProps> = ({
  project,
  onPhaseComplete,
  onRefresh,
}) => {
  const [rules, setRules] = useState<MappingRule[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [waitingOnly, setWaitingOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [detailRule, setDetailRule] = useState<MappingRule | null>(null);
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [toast, setToast] = useState('');

  const hasRules = project.summary_stats?.total_rules && project.summary_stats.total_rules > 0;

  useEffect(() => {
    if (hasRules) {
      loadRules();
    }
  }, [project.id, page, typeFilter, waitingOnly]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const filters: any = { page, limit };
      if (typeFilter !== 'ALL') filters.mapping_type = typeFilter;
      if (waitingOnly) filters.status = 'proposed';

      const data = await getMappings(project.id, filters);
      setRules(data.rules);
      setTotal(data.total);
    } catch (err: any) {
      console.error('Failed to load rules:', err);
      setToast('Could not load the account mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await generateMappings(project.id);
      setToast(`${result.rules_generated} mappings proposed · ${result.questions_generated} decisions raised`);
      onRefresh();
      await loadRules();
    } catch (err: any) {
      console.error('Mapping generation failed:', err);
      setToast(err.response?.data?.error || 'Mapping generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewDetail = async (ruleId: string) => {
    try {
      const detail = await getMappingDetail(project.id, ruleId);
      setDetailRule(detail);
    } catch (err) {
      console.error('Failed to load rule details:', err);
      setToast('Failed to load the mapping detail');
    }
  };

  const handleStatusChange = async (ruleId: string, newStatus: 'confirmed' | 'rejected') => {
    setUpdatingStatus(true);
    try {
      await updateMapping(project.id, ruleId, { status: newStatus });
      setToast(newStatus === 'confirmed' ? 'Mapping approved' : 'Mapping rejected');
      setDetailRule(null);
      await loadRules();
      onRefresh();
    } catch (err: any) {
      console.error('Update failed:', err);
      setToast(err.response?.data?.error || 'Update failed');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // "Approve all unchanged" — every proposed DIRECT rule project-wide.
  // Count comes from summary_stats (server-computed); fallback to the loaded page.
  const pageDirectProposed = useMemo(
    () => rules.filter(r => r.mapping_type === 'DIRECT' && r.status === 'proposed'),
    [rules]
  );
  const directProposedCount =
    project.summary_stats?.direct_proposed ?? pageDirectProposed.length;

  const handleApproveAllUnchanged = async () => {
    if (directProposedCount === 0) return;
    setConfirmingBulk(true);
    try {
      // No rule_ids → the server confirms every proposed DIRECT rule in the project
      const result = await bulkConfirmMappings(project.id);
      setToast(`${result.confirmed} unchanged mappings approved`);
      await loadRules();
      onRefresh();
    } catch (err: any) {
      console.error('Bulk confirm failed:', err);
      setToast(err.response?.data?.error || 'Bulk approval failed');
    } finally {
      setConfirmingBulk(false);
    }
  };

  const visibleRules = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter(r => {
      const code = r.sourceEntity?.account_code || '';
      const name = r.sourceEntity?.account_name || '';
      return code.toLowerCase().includes(q) || name.toLowerCase().includes(q);
    });
  }, [rules, search]);

  const waitingCount = project.summary_stats?.by_status?.proposed ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (!hasRules) {
    return (
      <div style={{ fontFamily: font.ui }}>
        <div style={{ ...panel, textAlign: 'center', padding: '48px 24px' }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: c.textPrimary }}>
            No account mappings yet
          </h2>
          <p style={{ margin: '0 auto 20px', maxWidth: 560, fontSize: 13, color: c.textBody, lineHeight: 1.6 }}>
            Generate the mapping to give every old account a proposed destination in the new chart.
            Each one is labeled with what changes — unchanged, splits in two, combined, needs new
            detail, booked differently, or no home yet.
          </p>
          <button
            type="button"
            style={{ ...btnPrimary(generating), fontSize: 14, padding: '11px 22px' }}
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Generating mappings…' : 'Generate mappings'}
          </button>
          {generating && (
            <p style={{ marginTop: 14, fontSize: 12, color: c.textSecondary }}>
              This may take a minute — proposing a destination for every account…
            </p>
          )}
        </div>
        <Toast message={toast} onClose={() => setToast('')} />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: font.ui, position: 'relative' }}>
      {/* Change-type summary */}
      {project.summary_stats && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {Object.entries(project.summary_stats.by_type || {}).map(([type, count]) => {
            const t = CHANGE_TYPES[type];
            return (
              <span
                key={type}
                title={t?.tip}
                style={{
                  ...pill(t?.bg || c.fill2, t?.fg || c.tableLabel),
                  padding: '5px 12px',
                }}
              >
                {t?.label || type} · {count as number}
              </span>
            );
          })}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <input
          type="search"
          aria-label="Search accounts"
          placeholder="Search account code or name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 260px', minWidth: 240, width: 'auto' }}
        />
        <button
          type="button"
          onClick={() => { setWaitingOnly(true); setPage(1); }}
          style={{
            ...(waitingOnly
              ? { ...btnSecondary(), background: c.textPrimary, color: '#fff', border: `1px solid ${c.textPrimary}` }
              : btnSecondary()),
            fontSize: 12.5,
          }}
        >
          Waiting on you · {waitingCount}
        </button>
        <button
          type="button"
          onClick={() => { setWaitingOnly(false); setPage(1); }}
          style={{
            ...(!waitingOnly
              ? { ...btnSecondary(), background: c.textPrimary, color: '#fff', border: `1px solid ${c.textPrimary}` }
              : btnSecondary()),
            fontSize: 12.5,
          }}
        >
          All rules
        </button>
        <label htmlFor="ca-type-filter" style={{ fontSize: 11.5, color: c.textSecondary, fontWeight: 700 }}>
          Change
        </label>
        <select
          id="ca-type-filter"
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
          style={{ ...inputStyle, width: 'auto', padding: '7px 10px', fontSize: 12.5 }}
        >
          {MAPPING_TYPES.map(t => (
            <option key={t} value={t}>
              {t === 'ALL' ? 'All changes' : CHANGE_TYPES[t]?.label || t}
            </option>
          ))}
        </select>
        <button
          type="button"
          style={{ ...btnGreen(confirmingBulk || directProposedCount === 0), marginLeft: 'auto' }}
          onClick={handleApproveAllUnchanged}
          disabled={confirmingBulk || directProposedCount === 0}
        >
          {confirmingBulk ? 'Approving…' : `✓ Approve all unchanged · ${directProposedCount}`}
        </button>
      </div>

      {/* Rules table */}
      <div style={{ ...panel, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, fontSize: 13, color: c.textSecondary }}>Loading mappings…</div>
        ) : visibleRules.length === 0 ? (
          <div style={{ padding: 32, fontSize: 13, color: c.textSecondary }}>
            No mappings match this view.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Old account</th>
                <th style={thStyle}>What changes</th>
                <th style={thStyle}>Confidence</th>
                <th style={thStyle}>New account(s)</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleRules.map(rule => (
                <tr
                  key={rule.id}
                  onClick={() => handleViewDetail(rule.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={tdStyle}>
                    <div style={{ fontFamily: font.mono, fontSize: 12.5, color: c.textSecondary }}>
                      {rule.sourceEntity?.account_code || rule.source_entity_id.substring(0, 8)}
                    </div>
                    {rule.sourceEntity?.account_name && (
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: c.textPrimary }}>
                        {rule.sourceEntity.account_name}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>{changePill(rule.mapping_type)}</td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: font.mono, fontSize: 11.5, color: c.textTertiary }}>
                      {(rule.confidence_score * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {rule.targets.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {rule.targets.map((t, i) => (
                          <span key={i} style={{ fontFamily: font.mono, fontSize: 12, color: c.textBody }}>
                            {t.targetEntity?.account_code || t.target_entity_id.substring(0, 8)}
                            {t.allocation_percentage ? ` (${t.allocation_percentage}%)` : ''}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: c.textTertiary }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>{statusPill(rule.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, justifyContent: 'center' }}>
          <button
            type="button"
            style={btnSecondary(page <= 1)}
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          <span style={{ fontSize: 12, color: c.textSecondary, fontFamily: font.mono }}>
            Page {page} of {totalPages} · {total} mappings
          </span>
          <button
            type="button"
            style={btnSecondary(page >= totalPages)}
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Detail drawer */}
      {detailRule && (
        <div
          onClick={() => setDetailRule(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.25)', zIndex: 200 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 440,
              maxWidth: '90vw',
              background: c.card,
              borderLeft: `1px solid ${c.border}`,
              boxShadow: '-8px 0 32px rgba(15, 23, 42, 0.12)',
              padding: 24,
              overflowY: 'auto',
              fontFamily: font.ui,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={eyebrow}>Mapping detail</div>
              <button
                type="button"
                aria-label="Close detail panel"
                onClick={() => setDetailRule(null)}
                style={{ background: 'transparent', border: 'none', fontSize: 16, fontWeight: 800, color: c.textSecondary, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {changePill(detailRule.mapping_type)}
              {statusPill(detailRule.status)}
              <span style={{ fontFamily: font.mono, fontSize: 11.5, color: c.textTertiary }}>
                {(detailRule.confidence_score * 100).toFixed(0)}% confident
              </span>
            </div>

            {/* Source account callout */}
            <div
              style={{
                background: c.blue.fillSoft,
                borderLeft: `3px solid ${c.blue.text2}`,
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 8,
              }}
            >
              <div style={{ ...eyebrow, color: c.blue.text, marginBottom: 4 }}>Old account (EBS)</div>
              <div style={{ fontFamily: font.mono, fontSize: 12.5, color: c.textSecondary }}>
                {detailRule.sourceEntity?.account_code}
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: c.textPrimary }}>
                {detailRule.sourceEntity?.account_name}
              </div>
            </div>

            <div style={{ textAlign: 'center', fontSize: 16, color: c.textTertiary, margin: '2px 0' }}>↓</div>

            {/* Target account callouts */}
            {detailRule.targets.length > 0 ? (
              detailRule.targets.map((t, i) => (
                <div
                  key={i}
                  style={{
                    background: c.green.fillSoft,
                    borderLeft: `3px solid ${c.green.text2}`,
                    borderRadius: 8,
                    padding: '12px 14px',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ ...eyebrow, color: c.green.text }}>New account (Cloud)</span>
                    {t.targetEntity?.attributes?.is_new ? (
                      <span style={pill(c.amber.fill, c.amber.text, c.amber.border)}>NEW</span>
                    ) : null}
                  </div>
                  <div style={{ fontFamily: font.mono, fontSize: 12.5, color: c.textSecondary }}>
                    {t.targetEntity?.account_code || t.target_entity_id.substring(0, 8)}
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: c.textPrimary }}>
                    {t.targetEntity?.account_name || '—'}
                  </div>
                  {(t.allocation_method || t.allocation_percentage) && (
                    <div style={{ fontSize: 11.5, color: c.textSecondary, marginTop: 4 }}>
                      {t.allocation_method || 'Allocation'}
                      {t.allocation_percentage ? ` · ${t.allocation_percentage}%` : ''}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div
                style={{
                  background: c.amber.fillSoft,
                  borderLeft: `3px solid ${c.amber.solid}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 8,
                  fontSize: 12.5,
                  color: c.amber.text,
                }}
              >
                No destination yet — this account still needs a home in the new chart.
              </div>
            )}

            {/* Rationale */}
            {detailRule.rationale && (
              <div style={{ background: c.fill2, borderRadius: 8, padding: '12px 14px', margin: '12px 0' }}>
                <div style={{ ...eyebrow, marginBottom: 4 }}>Why we propose this</div>
                <p style={{ margin: 0, fontSize: 12.5, color: c.textBody, lineHeight: 1.6 }}>
                  {detailRule.rationale}
                </p>
              </div>
            )}

            {/* Actions */}
            {detailRule.status === 'proposed' && (
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => handleStatusChange(detailRule.id, 'confirmed')}
                  disabled={updatingStatus}
                  style={{
                    fontFamily: font.ui, fontSize: 13, fontWeight: 700, color: '#fff',
                    background: c.green.text2, border: 'none', borderRadius: 999,
                    padding: '9px 20px', cursor: updatingStatus ? 'default' : 'pointer',
                  }}
                >
                  {updatingStatus ? 'Saving…' : '✓ Approve'}
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusChange(detailRule.id, 'rejected')}
                  disabled={updatingStatus}
                  style={btnSecondary(updatingStatus)}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Phase advance */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <button type="button" style={btnSecondary(generating)} onClick={handleGenerate} disabled={generating}>
          {generating ? 'Regenerating…' : 'Regenerate mappings'}
        </button>
        <button
          type="button"
          style={{ ...btnPrimary(), fontSize: 14, padding: '11px 22px' }}
          onClick={onPhaseComplete}
        >
          Go to decisions →
        </button>
      </div>

      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

export default MappingWorkbench;
